/**
 * Backward-compatible helpers for the (new, additive) per-component
 * `options[]` array - see memory MCP "TPCRM Voyager Pro CRM Reskin Project"
 * entry "QUOTE BUILDER PHASE 2" for full background.
 *
 * Every component keeps its existing top-level title/price/data/photos/
 * isOption/optionDescription/paymentTiming/optionalDepositAmount/
 * depositRequired/depositAmount/depositDueDate fields completely unchanged -
 * nothing about how those are read or written changes. `options[]` is purely
 * ADDITIVE: option[0] always mirrors those same top-level fields (kept in
 * sync by buildSavedComponent() on every save of the primary form), and any
 * options[1+] are extra agent-added alternatives for client-side comparison
 * (Figma's "clientChoose" pattern).
 *
 * Any existing code that doesn't know about `options[]` (quote-list
 * previews, commission calculations, PDF/print export, any client template
 * not yet updated to render the comparison UI) keeps reading the exact same
 * top-level fields it always has and is completely unaffected by any of
 * this - old quotes render identically to before, forever, even if they're
 * never re-saved. This module only exists to give NEW code a single,
 * consistent way to ask "what are this component's option(s)?" without
 * needing to special-case components that predate this feature.
 */

// True only when there's a genuine multi-choice comparison to show.
export function hasMultipleOptions(component) {
  return Array.isArray(component?.options) && component.options.length > 1;
}

// Build a single "primary" option view from a component's own top-level
// fields - used both as the fallback for legacy/untouched components (no
// `options` array at all) and to keep options[0] in sync on every save.
export function primaryOptionFromComponent(component) {
  const existingPrimary = Array.isArray(component?.options) ? component.options[0] : null;
  return {
    id: existingPrimary?.id || `${component?.id || 'opt'}-primary`,
    label: existingPrimary?.label || 'Option A',
    badge: existingPrimary?.badge || '',
    price: parseFloat(component?.price) || 0,
    description: component?.optionDescription || '',
    agentNotes: component?.agentNotes || '',
    agentCommission: component?.agentCommission ?? null,
    photoUrl: component?.photos?.[0]?.url || existingPrimary?.photoUrl || null,
    deposit: component?.depositRequired
      ? { depositAmount: parseFloat(component.depositAmount) || 0, balanceDueDate: component.depositDueDate || '' }
      : null,
  };
}

// Always returns a non-empty array - either the component's real
// `options[]` (if present) or a single option synthesized from its legacy
// top-level fields - so calling code never has to special-case "old" vs
// "new" components.
export function getComponentOptions(component) {
  if (Array.isArray(component?.options) && component.options.length > 0) {
    return component.options;
  }
  return [primaryOptionFromComponent(component)];
}

export function getOptionTotal(option) {
  return parseFloat(option?.price) || 0;
}

export function getOptionDueNow(option) {
  if (!option?.deposit || !(parseFloat(option.deposit.depositAmount) > 0)) return getOptionTotal(option);
  return parseFloat(option.deposit.depositAmount) || 0;
}

export function getOptionBalanceDue(option) {
  if (!option?.deposit || !(parseFloat(option.deposit.depositAmount) > 0)) return 0;
  return Math.max(0, getOptionTotal(option) - (parseFloat(option.deposit.depositAmount) || 0));
}

// Blank starting point for a new additional (non-primary) option.
export function blankAdditionalOption(index) {
  return {
    id: `${Date.now()}-${index}`,
    label: `Option ${String.fromCharCode(65 + index)}`,
    badge: '',
    price: 0,
    description: '',
    agentNotes: '',
    agentCommission: null,
    photoUrl: null,
    deposit: null,
  };
}
